import { useState } from 'react'
import { Box, Button, Paper, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import Header from 'components/Header'
import { CloseIcon, UndoIcon } from 'components/Icons'
import MultiFileUpload from 'components/MultiFileUpload'
import { uploadFile } from 'utils/file'
import { FILE_DELETED, MIN_PROOF_FILES, proofList, toProofItem } from 'utils/proofOfAddress'
import useSaveSection from '../useSaveSection'

export default function FilesSection({ request, onClose }) {
  const { t } = useTranslation()
  const { save, saving, setSaving } = useSaveSection(request)
  const original = proofList(request.proofOfAddress)
  const [files, setFiles] = useState(original)
  const [picked, setPicked] = useState([])

  const rejected = files.filter((file, index) => file.fileStatus === FILE_DELETED && original[index]?.fileStatus !== FILE_DELETED)
  const changed = picked.length > 0 || rejected.length > 0

  const reject = target => setFiles(prev => prev.map(file => (file === target ? { ...file, fileStatus: FILE_DELETED } : file)))
  const undo = target =>
    setFiles(prev => prev.map((file, index) => (file === target ? original[index] : file)))

  const items = [
    ...files
      .filter(file => file.fileStatus !== FILE_DELETED)
      .map(file => ({ key: `saved-${file.fileId}`, name: file.name, uuid: file.fileUuid, saved: file, removable: false })),
    ...picked.map((file, index) => ({
      key: `picked-${index}-${file.name}`,
      name: file.name,
      picked: file,
      flag: { label: 'New', color: 'info' },
    })),
  ]

  const renderActions = item =>
    item.saved && (
      <Button size="small" color="error" startIcon={<CloseIcon />} disabled={saving} onClick={() => reject(item.saved)}>
        {t('Reject')}
      </Button>
    )

  const onSave = async () => {
    setSaving(true)
    const uploaded = []
    for (const file of picked) {
      const row = await uploadFile(file)
      if (!row) {
        setSaving(false)
        return
      }
      uploaded.push(toProofItem(row, file))
    }
    if (await save({ proofOfAddress: [...files, ...uploaded] }, 'Proof of address updated')) onClose()
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Header title={t('Proof of Address')} small close={onClose} borderBottom />

      <Stack spacing={2} sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t('Reject a file to ask the parent for a new one, or upload files on behalf of the parent.')}
        </Typography>
        <MultiFileUpload
          items={items}
          onAdd={newFiles => setPicked(prev => [...prev, ...newFiles])}
          onRemove={item => setPicked(prev => prev.filter(file => file !== item.picked))}
          renderActions={renderActions}
          min={MIN_PROOF_FILES}
          disabled={saving}
        />

        {rejected.length > 0 && (
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: 'error.light' }}>
            <Typography variant="subtitle2" color="error.main" sx={{ mb: 0.5 }}>
              {t('Rejected ({{count}}), saved on Save', { count: rejected.length })}
            </Typography>
            {rejected.map(file => (
              <Stack key={file.fileId} direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }} title={file.name}>
                  {file.name || t('Uploaded file')}
                </Typography>
                <Button size="small" startIcon={<UndoIcon />} disabled={saving} onClick={() => undo(file)}>
                  {t('Undo')}
                </Button>
              </Stack>
            ))}
          </Paper>
        )}
      </Stack>

      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose}>{t('Cancel')}</Button>
        <Button variant="contained" disabled={saving || !changed} onClick={onSave}>
          {t('Save')}
        </Button>
      </Stack>
    </Box>
  )
}
